/**
 * useThreadMessages
 * Component-level hook for filtering thread-specific messages
 * Handles WebSocket message subscription and conversion to Message objects
 *
 * Subscribes to WebSocketContext and filters messages by threadId.
 * Maintains a local list of messages for the thread with deduplication.
 */

import { useState, useEffect, useRef } from 'preact/hooks';
import { useWebSocketContext } from '../contexts/WebSocketContext';
import type { Message } from '@zenmako/contracts/threads';
import type { WebSocketMessage } from '../hooks/useWebSocket';

/**
 * Sort messages by timestamp, with messageId as fallback
 * Messages may have timestamp as Date or messageId as numeric timestamp string
 */
function sortMessagesByTimestamp(messages: Message[]): Message[] {
  return messages.sort((a, b) => {
    const aTime = a.timestamp?.getTime() ?? parseInt(a.id) ?? 0;
    const bTime = b.timestamp?.getTime() ?? parseInt(b.id) ?? 0;
    return aTime - bTime;
  });
}

/**
 * Hook to manage thread-specific messages from WebSocket
 *
 * @param threadId - The thread ID to filter messages for
 * @param onFirstMessage - Optional callback when first message arrives in new thread
 * @returns Object with messages array and setMessages function
 */
export function useThreadMessages(threadId: string, onFirstMessage?: () => void) {
  const { subscribe } = useWebSocketContext();
  const [messages, setMessages] = useState<Message[]>([]);

  // Store pending tool-calls that are waiting for their tool-results
  // Key: toolCallId, Value: { toolCallId, toolName, args }
  const pendingToolCalls = useRef<Map<string, any>>(new Map());

  // Reset messages and pending tool calls when threadId changes
  useEffect(() => {
    setMessages([]);
    pendingToolCalls.current.clear();
  }, [threadId]);

  useEffect(() => {
    const handleMessage = (message: WebSocketMessage) => {
      // Filter: only process messages for THIS thread
      const payload = message.payload as any;
      if (payload.threadId !== threadId) {
        return; // Different thread - ignore
      }

      console.log('[useThreadMessages] Message for thread:', message.messageType, threadId, 'payload keys:', Object.keys(payload));

      // Helper to unwrap AI SDK's {type: "json", value: {...}} format
      // The REST API unwraps this before returning, but WebSocket sends the wrapped format
      const unwrapOutput = (output: any): any => {
        if (output && typeof output === 'object' && output.type === 'json' && output.value !== undefined) {
          return output.value;
        }
        return output;
      };

      // Handle new thread.message type
      if (message.messageType === 'thread.message') {
        // Special handling for tool/result@v1 messages - these have result and toolName but no content array
        if (payload.result && payload.toolName && !payload.content) {
          console.log('[useThreadMessages] Processing tool/result@v1 message:', payload.toolName, payload);

          // For internal orchestration tools (execute_todo_step, continue_todo_execution),
          // the ACTUAL tool calls are in executorResponse.toolCalls - we extract and display those
          // We don't skip these - they contain the real execution results

          // Create a message from executorResponse.text OR result.message as fallback
          const executorText = payload.result.executorResponse?.text;
          const resultMessage = payload.result.message;
          const displayText = executorText || resultMessage;

          if (displayText) {
            console.log('[useThreadMessages] Creating message from tool result:', displayText.substring(0, 50));

            // Extract ACTUAL tool calls from executorResponse.toolCalls if available
            // These are the real connector tools (coda_queryTable, core_verify, etc.)
            // NOT the wrapper tool (execute_todo_step)
            const executorToolCalls = payload.result.executorResponse?.toolCalls;
            let toolCalls: any[];

            if (executorToolCalls && Array.isArray(executorToolCalls) && executorToolCalls.length > 0) {
              // Use the actual tool calls from the executor
              console.log('[useThreadMessages] Using actual tool calls from executorResponse:', executorToolCalls.length, executorToolCalls);
              toolCalls = executorToolCalls.map((tc: any) => {
                // tc.result contains the full tool output including connectorId, toolId, requiresApproval, etc.
                // We need to pass it through directly, NOT wrap it
                const output = tc.result || {};

                // Extract connectorId and toolId from tool name if not in result
                // Tool name format: "connectorId_toolName" (e.g., "core_verify")
                let connectorId = output.connectorId;
                let toolId = output.toolId;
                if (!connectorId && tc.toolName?.includes('_')) {
                  connectorId = tc.toolName.split('_')[0];
                }
                if (!toolId && tc.toolName?.includes('_')) {
                  toolId = tc.toolName.split('_').slice(1).join('_');
                }

                console.log('[useThreadMessages] Tool call mapping:', {
                  id: tc.id,
                  toolName: tc.toolName,
                  hasResult: !!tc.result,
                  connectorId,
                  toolId,
                  requiresApproval: output.requiresApproval,
                });

                return {
                  id: tc.id || `tc-${Date.now()}`,
                  toolName: tc.toolName,
                  args: tc.args || {},
                  output: {
                    ...output,
                    connectorId,
                    toolId,
                  },
                  status: tc.status || 'completed',
                };
              });
            } else {
              // Fall back to wrapper tool name if no actual tool calls
              const toolCallId = payload.toolCallId || message.messageId || `tool-result-${Date.now()}`;

              // Extract connectorId and toolId from wrapper tool name
              let connectorId = payload.result?.connectorId;
              let toolId = payload.result?.toolId;
              if (!connectorId && payload.toolName?.includes('_')) {
                connectorId = payload.toolName.split('_')[0];
              }
              if (!toolId && payload.toolName?.includes('_')) {
                toolId = payload.toolName.split('_').slice(1).join('_');
              }

              toolCalls = [{
                id: toolCallId,
                toolName: payload.toolName,
                args: payload.toolInput || {},
                output: {
                  ...payload.result,
                  connectorId,
                  toolId,
                },
              }];
            }

            const newMessage: Message = {
              id: message.messageId || `tool-result-${Date.now()}`,
              role: 'assistant',
              content: displayText,
              timestamp: new Date(payload.timestamp || message.timestamp),
              agentId: payload.agentId,
              metadata: payload.metadata,
              toolCalls,
            };

            setMessages(prev => {
              // Check for duplicate by ID
              const existsById = prev.some(m => m.id === newMessage.id);
              if (existsById) {
                console.log('[useThreadMessages] Skipping duplicate tool result message (same ID)');
                return prev;
              }

              // Replace any earlier message that has the same toolCallId (bare tool-call without output)
              // This prevents duplicates when tool-call and tool/result@v1 events fire separately
              const newToolCallIds = new Set(newMessage.toolCalls?.map(tc => tc.id).filter(Boolean));
              if (newToolCallIds.size > 0) {
                const existingIdx = prev.findIndex(m =>
                  m.toolCalls?.some(tc => tc.id && newToolCallIds.has(tc.id))
                );
                if (existingIdx !== -1) {
                  console.log('[useThreadMessages] Replacing earlier bare tool-call message with enriched result');
                  const updated = [...prev];
                  updated[existingIdx] = newMessage;
                  return sortMessagesByTimestamp(updated);
                }
              }

              console.log('[useThreadMessages] Adding tool result message to state');
              return sortMessagesByTimestamp([...prev, newMessage]);
            });
          } else {
            console.log('[useThreadMessages] tool/result@v1 has no displayable text, skipping');
          }
          // Always return after handling tool/result - don't fall through
          return;
        }

        // Determine role from metadata or userId
        const role = payload.metadata?.role || (payload.userId ? 'user' : 'assistant');
        console.log('[useThreadMessages] Processing thread.message, role:', role, 'content type:', Array.isArray(payload.content) ? 'array' : typeof payload.content);

        // Debug: log content array parts if present
        if (Array.isArray(payload.content)) {
          const partTypes = payload.content.map((p: any) => p.type);
          console.log('[useThreadMessages] Content parts types:', partTypes);
        }

        // Extract content and tool calls
        let content = '';
        let toolCalls: any[] | undefined;

        // If content is an array (ContentPart[]), extract text and tool calls
        if (Array.isArray(payload.content)) {
          // Extract text content
          const textParts = payload.content.filter((part: any) => part.type === 'text');
          content = textParts.map((part: any) => part.text || '').join('');

          // Extract tool calls and results
          const toolCallParts = payload.content.filter((part: any) => part.type === 'tool-call');
          const toolResultParts = payload.content.filter((part: any) => part.type === 'tool-result');

          console.log('[useThreadMessages] Array content - textParts:', textParts.length, 'toolCallParts:', toolCallParts.length, 'toolResultParts:', toolResultParts.length);

          // Store tool-calls that don't have results yet (they'll arrive in a separate message)
          // BUT STILL display tool-call messages so user sees activity
          if (toolCallParts.length > 0) {
            console.log('[useThreadMessages] Processing tool-call parts');
            toolCallParts.forEach((tc: any) => {
              pendingToolCalls.current.set(tc.toolCallId, {
                toolCallId: tc.toolCallId,
                toolName: tc.toolName,
                args: tc.input || {},
              });
            });
            // Continue processing - don't return early anymore
          }

          // Build tool calls array by merging tool-calls and tool-results
          if (toolCallParts.length > 0 || toolResultParts.length > 0) {
            toolCalls = [];

            // Process tool-calls in this message
            toolCallParts.forEach((tc: any) => {
              const result = toolResultParts.find((tr: any) => tr.toolCallId === tc.toolCallId);
              toolCalls!.push({
                id: tc.toolCallId,
                toolName: tc.toolName,
                args: tc.input || {},
                status: 'completed' as const,
                output: unwrapOutput(result?.output)
              });
            });

            // Process tool-results, merging with pending tool-calls if needed
            toolResultParts.forEach((tr: any) => {
              // Skip if already processed above
              if (toolCallParts.find((tc: any) => tc.toolCallId === tr.toolCallId)) {
                return;
              }

              // Look for matching pending tool-call
              const pendingToolCall = pendingToolCalls.current.get(tr.toolCallId);

              if (pendingToolCall) {
                // Merge with pending tool-call
                console.log('[useThreadMessages] Merging tool-result with pending tool-call:', tr.toolCallId);
                toolCalls!.push({
                  id: tr.toolCallId,
                  toolName: tr.toolName,
                  args: pendingToolCall.args || {},
                  status: 'completed' as const,
                  output: unwrapOutput(tr.output)
                });
                // Remove from pending now that it's matched
                pendingToolCalls.current.delete(tr.toolCallId);
              } else {
                // No matching tool-call found (shouldn't happen, but handle gracefully)
                console.warn('[useThreadMessages] Tool-result without matching tool-call:', tr.toolCallId);
                toolCalls!.push({
                  id: tr.toolCallId,
                  toolName: tr.toolName,
                  args: {},
                  status: 'completed' as const,
                  output: unwrapOutput(tr.output)
                });
              }
            });
          }
        } else {
          // Content is a string
          content = payload.content || payload.response || '';
          toolCalls = payload.toolCalls || payload.metadata?.toolCalls;
        }

        // Build metadata with user info
        let messageMetadata = payload.metadata;
        if (payload.userId && !messageMetadata?.user) {
          // Create user metadata if it doesn't exist (backward compatibility)
          messageMetadata = {
            ...messageMetadata,
            user: {
              userId: payload.userId,
              email: payload.userEmail, // Fallback for old events
            },
          };
        }

        const newMessage: Message = {
          id: payload.messageId || message.messageId,
          role: role,
          content: content,
          timestamp: new Date(payload.timestamp || message.timestamp),
          agentId: payload.agentId,
          toolCalls: toolCalls,
          metadata: messageMetadata,
        };

        console.log('[useThreadMessages] Created newMessage:', { id: newMessage.id, role: newMessage.role, contentLength: newMessage.content?.length, hasToolCalls: !!newMessage.toolCalls });

        // Deduplicate - compare role, content, AND toolCalls
        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.role === newMessage.role && lastMsg.content === newMessage.content) {
            // Also check if toolCalls are the same
            const lastToolCallsStr = JSON.stringify(lastMsg.toolCalls || []);
            const newToolCallsStr = JSON.stringify(newMessage.toolCalls || []);
            if (lastToolCallsStr === newToolCallsStr) {
              console.log('[useThreadMessages] Skipping duplicate message');
              return prev;
            }
          }

          // If this is the first assistant message in a new thread, trigger refresh
          if (newMessage.role === 'assistant' && prev.length <= 1 && onFirstMessage) {
            console.log('[useThreadMessages] First assistant message in new thread - triggering refresh');
            // Use setTimeout to avoid state update during render
            setTimeout(() => onFirstMessage(), 100);
          }

          return sortMessagesByTimestamp([...prev, newMessage]);
        });
      }
      // Handle legacy user.message type
      else if (message.messageType === 'user.message') {
        const newMessage: Message = {
          id: message.messageId,
          role: 'user',
          content: payload.content,
          timestamp: new Date(message.timestamp),
          metadata: {
            user: {
              userId: payload.userId,
              username: payload.username,
            },
          },
        };

        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.role === 'user' && lastMsg.content === newMessage.content) {
            console.log('[useThreadMessages] Skipping duplicate user message');
            return prev;
          }
          return sortMessagesByTimestamp([...prev, newMessage]);
        });
      }
      // Handle legacy agent.response type
      else if (message.messageType === 'agent.response') {
        const newMessage: Message = {
          id: message.messageId,
          role: 'assistant',
          content: payload.content,
          timestamp: new Date(message.timestamp),
          agentId: payload.agentId,
        };

        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.role === 'assistant' && lastMsg.content === newMessage.content) {
            console.log('[useThreadMessages] Skipping duplicate assistant message');
            return prev;
          }
          return sortMessagesByTimestamp([...prev, newMessage]);
        });
      }
      // Handle tool execution result messages (execution.completed/approval_required/error)
      // These come from tool/result@v1 events via the websocket event-handler
      // For internal orchestration tools, extract the actual tool calls from result.executorResponse.toolCalls
      else if (message.messageType === 'execution.completed' ||
               message.messageType === 'execution.approval_required' ||
               message.messageType === 'execution.error') {
        console.log('[useThreadMessages] Processing execution result:', message.messageType, payload.toolName);

        // Skip if no toolName - these are just internal signals
        const toolName = payload.toolName as string;
        if (!toolName) {
          console.log('[useThreadMessages] Skipping execution message with no toolName');
          return;
        }

        const status = message.messageType === 'execution.completed' ? 'completed' :
                       message.messageType === 'execution.approval_required' ? 'pending' :
                       'error';

        // For internal orchestration tools, extract ACTUAL tool calls from result.executorResponse.toolCalls
        const internalToolNames = ['continue_todo_execution', 'execute_todo_step'];
        const executorToolCalls = payload.result?.executorResponse?.toolCalls;

        if (internalToolNames.includes(toolName) && executorToolCalls && Array.isArray(executorToolCalls) && executorToolCalls.length > 0) {
          console.log('[useThreadMessages] Extracting actual tool calls from internal tool:', toolName, executorToolCalls.length);

          // Create messages for each actual tool call
          const toolCalls = executorToolCalls.map((tc: any) => {
            const output = tc.result || {};
            let connectorId = output.connectorId;
            let toolId = output.toolId;
            if (!connectorId && tc.toolName?.includes('_')) {
              connectorId = tc.toolName.split('_')[0];
            }
            if (!toolId && tc.toolName?.includes('_')) {
              toolId = tc.toolName.split('_').slice(1).join('_');
            }

            return {
              id: tc.id || `tc-${Date.now()}`,
              toolName: tc.toolName,
              args: tc.args || {},
              output: { ...output, connectorId, toolId },
              status: tc.status || status,
            };
          });

          const displayText = payload.result?.executorResponse?.text || payload.result?.message || '';

          const newMessage: Message = {
            id: message.messageId || `exec-${Date.now()}`,
            role: 'assistant',
            content: displayText,
            timestamp: new Date(payload.timestamp || message.timestamp),
            agentId: payload.agentId,
            toolCalls: toolCalls as any,
          };

          setMessages(prev => {
            const exists = prev.some(m => m.id === newMessage.id);
            if (exists) {
              console.log('[useThreadMessages] Skipping duplicate execution result:', newMessage.id);
              return prev;
            }
            return sortMessagesByTimestamp([...prev, newMessage]);
          });
          return;
        }

        // For regular (non-internal) tools, create a simple message
        const toolCallId = payload.toolCallId || message.messageId || `toolcall-${Date.now()}`;

        const newMessage: Message = {
          id: message.messageId || `exec-${Date.now()}`,
          role: 'assistant',
          content: '',
          timestamp: new Date(payload.timestamp || message.timestamp),
          agentId: payload.agentId,
          toolCalls: [{
            id: toolCallId,
            toolName: toolName,
            args: payload.toolInput || payload.result?.args || {},
            status: status,
            output: payload.result,
          } as any],
        };

        setMessages(prev => {
          const exists = prev.some(m =>
            m.toolCalls?.some(tc => tc.id === toolCallId)
          );
          if (exists) {
            console.log('[useThreadMessages] Skipping duplicate tool result:', toolCallId);
            return prev;
          }
          return sortMessagesByTimestamp([...prev, newMessage]);
        });
      }
    };

    const unsubscribe = subscribe(handleMessage);
    return () => unsubscribe();
  }, [threadId, subscribe, onFirstMessage]);

  return { messages, setMessages };
}
